import { Box, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useRef, useState } from "react";
import { Submission, SUBMISSION_STATUS, CHIPS_COLOR } from "../../types/submissions";
import { User } from "../../types/users";
import { useUpdateSubmissionMutation } from "../../services/submissions.service";
import KanbanColumnShell from "../common/KanbanColumnShell";
import SubmissionsKanbanCard from "./SubmissionsKanbanCard";
import SubmissionForm from "./SubmissionForm";

const COLUMN_ORDER = Object.values(SUBMISSION_STATUS);

function SubmissionsKanban({ rows, users }: { rows: Submission[]; users: User[] }) {
  const [updateSubmission] = useUpdateSubmissionMutation();
  const draggingId = useRef<string | null>(null);
  const [search, setSearch] = useState("");
  const [editSubmission, setEditSubmission] = useState<Submission | null>(null);

  const filtered = rows.filter((s) => {
    const q = search.toLowerCase();
    return (
      !q ||
      s.fullName?.toLowerCase().includes(q) ||
      s.refNo?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q)
    );
  });

  const grouped = COLUMN_ORDER.reduce<Record<string, Submission[]>>((acc, status) => {
    acc[status] = filtered.filter((s) => s.status === status);
    return acc;
  }, {});

  const handleDrop = (newStatus: string) => {
    if (!draggingId.current) return;
    const submission = rows.find((s) => s._id === draggingId.current);
    draggingId.current = null;
    if (!submission || submission.status === newStatus) return;
    updateSubmission({ ...submission, status: newStatus as keyof typeof SUBMISSION_STATUS });
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search submissions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={2} sx={{ ml: "auto !important" }}>
          <Typography variant="body2" color="text.secondary">
            <b>{filtered.length}</b> submissions
          </Typography>
        </Stack>
      </Stack>

      {/* Board */}
      <Box
        sx={{
          flex: 1,
          overflowX: "auto",
          overflowY: "hidden",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-track": { bgcolor: "#F1F5F9", borderRadius: 999 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 999 },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ minWidth: "max-content", height: "100%", px: 0.5, py: 0.25 }}
        >
          {COLUMN_ORDER.map((status) => (
            <KanbanColumnShell
              key={status}
              status={status}
              chipColor={(CHIPS_COLOR[status] as any) ?? "default"}
              count={grouped[status]?.length ?? 0}
              width={240}
              onDrop={() => handleDrop(status)}
            >
              {(grouped[status] ?? []).map((s) => (
                <SubmissionsKanbanCard
                  key={s._id}
                  submission={s}
                  users={users}
                  onDragStart={(id) => { draggingId.current = id; }}
                  onClick={setEditSubmission}
                />
              ))}
            </KanbanColumnShell>
          ))}
        </Stack>
      </Box>

      {editSubmission && (
        <SubmissionForm
          open
          edit
          selectedSubmission={editSubmission}
          users={users}
          handleClose={() => setEditSubmission(null)}
          submitHanlder={(updated: Submission) => {
            updateSubmission(updated).then(() => setEditSubmission(null));
          }}
        />
      )}
    </Box>
  );
}

export default SubmissionsKanban;
